import type { CapturedRequest, EndpointInfo } from './api.js';
import { API_PREFIX } from './prefix.js';
import { createSseParser } from './sse.js';

/**
 * The inspector's live feed.
 *
 * Built on `fetch` rather than `EventSource` for two reasons that both matter
 * here: `EventSource` cannot send an `Authorization` header, so the token would
 * have to travel in the URL and end up in proxy logs; and its reconnection is
 * a fixed interval with no way to observe it, whereas the connection indicator
 * this UI promises needs to know the difference between "connected", "coming
 * back" and "given up". The cost is doing the resume by hand — which the server
 * makes cheap by tagging every frame with the request's `seq`.
 */

export type StreamStatus =
  | { kind: 'connecting' }
  | { kind: 'open' }
  /** Down, but coming back. `attempt` drives the "reintentando" copy. */
  | { kind: 'reconnecting'; attempt: number; retryInMs: number }
  /** Down for good: a bad token or a gone endpoint. Retrying cannot help. */
  | { kind: 'failed'; reason: string };

export interface StreamHandlers {
  onRequest: (request: CapturedRequest) => void;
  onCleared: () => void;
  /** Fired on every (re)connection, with what the server knows right now. */
  onReady: (info: { endpoint: EndpointInfo; backfilled: number; gapTooBig: boolean }) => void;
  onStatus: (status: StreamStatus) => void;
}

export interface StreamOptions extends StreamHandlers {
  slug: string;
  token: string;
  /** Resume point. Frames at or below this are not re-sent. */
  lastSeq?: number | undefined;
}

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 15_000;

/**
 * Exponential backoff with jitter. The jitter is not decoration: without it,
 * every inspector open on the same endpoint reconnects in lockstep after an
 * outage and hits the server as one spike.
 */
export function retryDelay(attempt: number, random: () => number = Math.random): number {
  const capped = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
  return Math.round(capped * (0.5 + random() * 0.5));
}

/** Opens the stream and keeps it open. Returns the function that stops it. */
export function openRequestStream(options: StreamOptions): () => void {
  const { slug, token, onRequest, onCleared, onReady, onStatus } = options;

  let lastSeq = options.lastSeq;
  let attempt = 0;
  let stopped = false;
  let controller: AbortController | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  /** Resolves the current backoff early, so "online" does not wait it out. */
  let wakeUp: (() => void) | undefined;

  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      retryTimer = setTimeout(resolve, ms);
      wakeUp = () => {
        clearTimeout(retryTimer);
        wakeUp = undefined;
        resolve();
      };
    });

  // The browser knows the network came back long before a timer would find out.
  const onOnline = (): void => wakeUp?.();
  window.addEventListener('online', onOnline);

  const run = async (): Promise<void> => {
    while (!stopped) {
      controller = new AbortController();

      try {
        onStatus({ kind: 'connecting' });
        await connect(slug, token, lastSeq, controller.signal, {
          onOpen: () => {
            attempt = 0;
            onStatus({ kind: 'open' });
          },
          onRequest: (request) => {
            lastSeq = Math.max(lastSeq ?? 0, request.seq);
            onRequest(request);
          },
          onCleared,
          onReady,
        });

        // The server closed a healthy stream — a redeploy, most likely.
        // Reconnect from where we were.
      } catch (error) {
        if (stopped) return;

        if (error instanceof FatalStreamError) {
          onStatus({ kind: 'failed', reason: error.message });
          return;
        }
        // Anything else is transient: a dropped connection, a 5xx, a timeout.
      }

      if (stopped) return;

      attempt += 1;
      const delay = retryDelay(attempt);
      onStatus({ kind: 'reconnecting', attempt, retryInMs: delay });
      await sleep(delay);
    }
  };

  void run();

  return () => {
    stopped = true;
    window.removeEventListener('online', onOnline);
    clearTimeout(retryTimer);
    controller?.abort();
  };
}

/** A failure that retrying cannot fix: the token is wrong or the endpoint is gone. */
class FatalStreamError extends Error {}

interface ConnectHandlers {
  onOpen: () => void;
  onRequest: (request: CapturedRequest) => void;
  onCleared: () => void;
  onReady: StreamHandlers['onReady'];
}

async function connect(
  slug: string,
  token: string,
  lastSeq: number | undefined,
  signal: AbortSignal,
  handlers: ConnectHandlers,
): Promise<void> {
  const response = await fetch(`${API_PREFIX}/endpoints/${slug}/stream`, {
    signal,
    headers: {
      accept: 'text/event-stream',
      authorization: `Bearer ${token}`,
      ...(lastSeq === undefined ? {} : { 'last-event-id': String(lastSeq) }),
    },
  });

  if (response.status === 401) {
    throw new FatalStreamError('El token no es válido para este endpoint.');
  }
  // 410 is an expired endpoint that the cleanup job has not deleted yet; the
  // stream cannot resume it any more than a 404 can, so both are fatal.
  if (response.status === 404 || response.status === 410) {
    throw new FatalStreamError('El endpoint ya no existe o ha caducado.');
  }
  if (!response.ok || !response.body) {
    throw new Error(`stream failed with HTTP ${response.status}`);
  }

  handlers.onOpen();

  const parser = createSseParser();
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;

      for (const message of parser.push(value)) {
        dispatch(message.event, message.data, handlers);
      }
    }
  } finally {
    reader.cancel().catch(() => {
      // Already torn down by the abort; nothing to clean up.
    });
  }
}

function dispatch(event: string, data: string, handlers: ConnectHandlers): void {
  let payload: unknown;

  try {
    payload = JSON.parse(data);
  } catch {
    // A frame we cannot read is not worth killing a working stream over.
    return;
  }

  if (event === 'request') {
    handlers.onRequest(payload as CapturedRequest);
    return;
  }

  if (event === 'cleared') {
    handlers.onCleared();
    return;
  }

  if (event === 'ready') {
    const ready = payload as {
      endpoint: EndpointInfo;
      backfilled: number;
      backfillTruncated: boolean;
    };
    handlers.onReady({
      endpoint: ready.endpoint,
      backfilled: ready.backfilled,
      gapTooBig: ready.backfillTruncated,
    });
  }
}
